import { useEffect, useRef } from "react";
import { toast } from "sonner";

/**
 * Drives Zero connection-state toasts for the app shell.
 *
 * Three pieces of behavior coexist on a shared set of refs that gate
 * each effect against its peers:
 *
 * 1. **First-connect-stale banner** — after 20s of waiting without a
 *    successful first connect, surface a permanent error toast hinting
 *    the client may be out of date. Third-line defense for outages
 *    where the connection times out silently rather than receiving a
 *    schema/version mismatch from the server (the symptom of the
 *    2026-04-28 outage from PRs #1065 / #1069 that dropped Zero schema
 *    columns without bumping `MIN_DESKTOP_VERSION`).
 *
 * 2. **Disconnect toast** — after a successful first connection, a
 *    transient disconnect debounces for 2.5s before showing
 *    "Connection lost — reconnecting..." (suppresses HMR / network
 *    jitter blips that recover before the timer fires). The toast is
 *    suppressed entirely when the document is hidden so backgrounded
 *    tabs don't queue stale notices.
 *
 * 3. **Reconnect toast** — when reconnecting after a visible disconnect
 *    (i.e. one whose toast actually showed), dismiss the disconnect
 *    toast and replace it with a 3s "Back online" success toast.
 *
 * The `hasConnectedRef` / `showedDisconnectToastRef` /
 * `disconnectToastTimerRef` triplet is what coordinates these — each
 * effect reads the others' refs to decide whether to fire.
 *
 * Renders nothing on its own; purely effectful.
 */
export function useConnectionStatusToasts(isConnected: boolean) {
  const hasConnectedRef = useRef(false);
  const disconnectToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const showedDisconnectToastRef = useRef(false);

  // Toast on connection state changes — only after the first successful
  // connection so the initial connect doesn't show "Back online".
  useEffect(() => {
    if (isConnected) {
      if (disconnectToastTimerRef.current) {
        clearTimeout(disconnectToastTimerRef.current);
        disconnectToastTimerRef.current = null;
      }
      if (!hasConnectedRef.current) {
        // First connection since mount — no toast
        hasConnectedRef.current = true;
        return;
      }
      // Recovered from a real disconnect that was visible to the user.
      if (showedDisconnectToastRef.current) {
        showedDisconnectToastRef.current = false;
        toast.dismiss("connection-status");
        toast.success("Back online", { duration: 3000 });
      }
      return;
    }
    if (!hasConnectedRef.current || disconnectToastTimerRef.current) return;

    // Debounce transient reconnect blips (common during HMR/network
    // jitter). In dev we use a longer debounce because Vite's HMR
    // routinely drops the WS for ~1-2s during file rebuilds and the
    // disconnect toast flashing on every save trains the user to
    // ignore reconnect notices for real outages too.
    const debounceMs = import.meta.env.DEV ? 5000 : 2500;
    disconnectToastTimerRef.current = setTimeout(() => {
      disconnectToastTimerRef.current = null;
      if (document.hidden) return;
      showedDisconnectToastRef.current = true;
      toast.error("Connection lost — reconnecting...", {
        id: "connection-status",
        duration: Infinity,
      });
    }, debounceMs);

    return () => {
      if (disconnectToastTimerRef.current) {
        clearTimeout(disconnectToastTimerRef.current);
        disconnectToastTimerRef.current = null;
      }
    };
  }, [isConnected]);

  // First-connect-never-succeeds banners. The reactive Zero
  // `onUpdateNeeded` callback only fires when the server explicitly
  // reports a schema or version mismatch. When the connection just
  // times out without any server-side response (the symptom of the
  // 2026-04-28 outage — PRs #1065 / #1069 dropped Zero schema columns
  // without bumping MIN_DESKTOP_VERSION first), the silent "Loading…"
  // state has no user-facing signal.
  //
  // Two-stage timeline:
  //   t=5s  → subtle reassurance ("Still connecting…") — covers the
  //          "did I lose my network?" silence on slow first-connects
  //          without scaring the user.
  //   t=8s  → the alarming banner that suggests a reload + update path.
  //          Previously fired at t=20s, which was long enough that
  //          beta testers consistently assumed the app had hung.
  //
  // The early reassurance dismisses itself when the alarm fires (or on
  // a successful connect) so the toaster never shows both at once.
  useEffect(() => {
    if (isConnected || hasConnectedRef.current) return;
    const reassureId = "first-connect-still-loading";
    const alarmId = "first-connect-stale";

    const reassureT = setTimeout(() => {
      toast("Still connecting to Ano…", {
        id: reassureId,
        duration: Infinity,
      });
    }, 5_000);

    const alarmT = setTimeout(() => {
      // Replace the subtle reassurance with the actionable alarm.
      toast.dismiss(reassureId);
      toast.error(
        "Still connecting to Ano… If this persists, your app may be out of date — please reload (or check for an update).",
        { id: alarmId, duration: Infinity },
      );
    }, 8_000);

    return () => {
      clearTimeout(reassureT);
      clearTimeout(alarmT);
      toast.dismiss(reassureId);
      toast.dismiss(alarmId);
    };
  }, [isConnected]);
}
